import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { SplitComponent } from 'angular-split';
import { AppPageConfig } from '../app-page/app-page.component';

export interface AppSplitPageConfig extends AppPageConfig {
  appPageClass?: string;

  left?: {
    collapseThreshold: number;
    width: number;
    maxWidth?: number;
    collapsedWidth?: number;
  };
  right?: {
    collapseThreshold: number;
    width: number;
    maxWidth?: number;
  };
}

interface SplitOutputEvent {
  gutterNum: number;
  sizes: Array<number | string>;
}

// This component leverages: https://angular-split.github.io/
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-app-split-page',
  templateUrl: './app-split-page.component.html',
  styleUrls: ['./app-split-page.component.scss'],
  standalone: false,
})
export class AppSplitPageComponent implements AfterViewInit, OnDestroy {
  @HostBinding('class') styles = 'bg-base-light';
  @Input() config!: AppSplitPageConfig;

  @ViewChild('split') split?: SplitComponent;

  leftCollapsed = false;
  leftShouldClose = false;
  transitioningLeft = false;
  @ViewChild('leftButtonOverlay') leftButtonOverlay?: CdkConnectedOverlay;
  leftOverlayOpen = true;
  private leftPaneOffset = 0;
  private leftOffsetWatcher?: number;

  rightCollapsed = false;
  rightShouldClose = false;
  transitioningRight = false;
  @ViewChild('rightButtonOverlay') rightButtonOverlay?: CdkConnectedOverlay;

  private lastExpandedSize = 0;
  private leftSize = 0;

  constructor(private ngZone: NgZone) {}

  get leftCollapsedWidth() {
    return this.config.left?.collapsedWidth ?? 56;
  }

  ngAfterViewInit() {
    // Ensure the toggle anchor is positioned after initial render
    setTimeout(() => this.updateLeftOverlayPosition());

    this.split?.dragProgress$.subscribe(progress => {
      // LEFT
      if (progress.gutterNum === 1 && this.config?.left) {
        this.updateLeftOverlayPosition();
        const leftSize = this.normalizeSize(progress.sizes[0], this.leftSize || this.config.left.width || 0);
        if (this.config.left?.collapseThreshold && leftSize <= this.config.left.collapseThreshold)
          this.ngZone.run(() => (this.leftShouldClose = true));
        else this.ngZone.run(() => (this.leftShouldClose = false));
      }
      // RIGHT
      if (progress.gutterNum === 2 || (progress.gutterNum === 1 && (!this.config.left || this.leftCollapsed))) {
        this.rightButtonOverlay?.overlayRef.updatePosition();
        const rightIndex = this.config?.left && !this.leftCollapsed ? 2 : 1;
        const rightSize = this.normalizeSize(progress.sizes[rightIndex], 0);
        if (this.config.right?.collapseThreshold && rightSize <= this.config.right.collapseThreshold)
          this.ngZone.run(() => (this.rightShouldClose = true));
        else this.ngZone.run(() => (this.rightShouldClose = false));
      }
    });

    // Track horizontal shifts (e.g., when the primary nav collapses) so the anchor follows
    this.ngZone.runOutsideAngular(() => {
      this.leftOffsetWatcher = window.setInterval(() => {
        const origin = this.leftButtonOverlay?.origin;
        if (!origin) return;
        const native: Element | null =
          origin instanceof CdkOverlayOrigin
            ? origin.elementRef.nativeElement
            : origin instanceof ElementRef
              ? origin.nativeElement
              : origin instanceof Element
                ? origin
                : ((origin as { nativeElement?: Element }).nativeElement ?? null);
        if (!native) return;
        const { left } = native.getBoundingClientRect();
        if (left !== this.leftPaneOffset) {
          this.leftPaneOffset = left;
          this.ngZone.run(() => this.updateLeftOverlayPosition());
        }
      }, 200);
    });
  }

  ngOnDestroy() {
    if (this.leftOffsetWatcher) window.clearInterval(this.leftOffsetWatcher);
  }

  checkClose(event: SplitOutputEvent) {
    const leftSizeFromEvent = this.normalizeSize(event.sizes[0], this.leftSize || this.config.left?.width || 0);
    const rightIndex = this.config?.left && !this.leftCollapsed ? 2 : 1;
    const rightSizeFromEvent = this.normalizeSize(event.sizes[rightIndex], 0);

    this.lastExpandedSize = leftSizeFromEvent || this.lastExpandedSize;
    if (!this.leftCollapsed) this.leftSize = leftSizeFromEvent;

    // If the user manually drags the gutter to expand while collapsed, reopen the pane
    if (this.leftCollapsed && leftSizeFromEvent > this.leftCollapsedWidth + 1) {
      this.leftCollapsed = false;
      this.leftShouldClose = false;
      this.transitioningLeft = true;
      this.leftSize = leftSizeFromEvent;
      this.lastExpandedSize = leftSizeFromEvent;
      this.reopenLeftOverlay();
      // Ensure the button is visible even if no transitionEnd event fires
      setTimeout(() => (this.transitioningLeft = false), 150);
    }

    if (this.leftShouldClose) {
      this.leftCollapsed = true;
      this.leftShouldClose = false;
      if (leftSizeFromEvent > 0) this.transitioningLeft = true;
      this.leftSize = this.leftCollapsedWidth;
      this.reopenLeftOverlay();
    }
    if (this.rightShouldClose) {
      this.rightCollapsed = true;
      this.rightShouldClose = false;
      if (rightSizeFromEvent > 0) this.transitioningRight = true;
    }
  }

  toggleLeftCollapsed() {
    const defaultLeftSize = this.config.left?.width || this.leftCollapsedWidth;
    if (!this.leftCollapsed) this.lastExpandedSize = this.leftSize || this.lastExpandedSize || defaultLeftSize;
    this.leftCollapsed = !this.leftCollapsed;
    this.transitioningLeft = true;
    this.leftSize = this.leftCollapsed ? this.leftCollapsedWidth : this.lastExpandedSize || defaultLeftSize;
    this.reopenLeftOverlay();
  }

  get currentLeftSize() {
    return this.leftSize || (this.leftCollapsed ? this.leftCollapsedWidth : this.config.left?.width || 0);
  }

  private updateLeftOverlayPosition() {
    // Update after view updates to keep the chevron flush to the pane edge
    setTimeout(() => this.leftButtonOverlay?.overlayRef?.updatePosition());
  }

  private reopenLeftOverlay() {
    this.leftOverlayOpen = false;
    // Reattach next tick so the origin reflects the new pane size
    setTimeout(() => {
      this.leftOverlayOpen = true;
      this.updateLeftOverlayPosition();
    });
  }

  private normalizeSize(value: number | string | undefined, fallback: number) {
    const numeric = typeof value === 'string' ? Number(value) : value;
    return Number.isFinite(numeric) ? (numeric as number) : fallback;
  }
}
